import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, TrendingUp, Calendar } from "lucide-react";
import { api } from "../lib/api";

const LEVELS = [
  { name: "Anfänger", min: 0 },
  { name: "Junior Seller", min: 100 },
  { name: "Senior Seller", min: 300 },
  { name: "Elite Seller", min: 700 },
  { name: "Novatus Master", min: 1500 },
];

const Progress = () => {
  const [progress, setProgress] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/me/progress");
      setProgress(data);
    })();
  }, []);

  if (!progress) return <div className="p-12 text-center text-novatus-text-3">Lade...</div>;

  const xp = progress.xp || 0;
  const currentIdx = LEVELS.reduce((acc, l, i) => xp >= l.min ? i : acc, 0);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
      <div className="animate-slide-up">
        <span className="label-eyebrow">Fortschritt</span>
        <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight text-white mt-2">
          Dein Weg zum <span className="text-novatus-green">Master.</span>
        </h1>
        <p className="text-novatus-text-2 mt-3 max-w-2xl">Jedes Training bringt dich weiter. Schau dir deine Reise an.</p>
      </div>

      <div className="nv-card mt-8 p-6 sm:p-9 animate-slide-up delay-1" data-testid="level-path-card">
        <div className="flex items-center gap-2 mb-6">
          <Trophy className="w-5 h-5 text-novatus-green" />
          <h2 className="font-display font-bold text-xl text-white">Level-Pfad</h2>
        </div>
        <div className="relative">
          <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-novatus" />
          {LEVELS.map((l, i) => {
            const reached = i <= currentIdx;
            const current = i === currentIdx;
            return (
              <div key={l.name} className="relative flex items-center gap-4 py-3" data-testid={`level-${i}`}>
                <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center font-display font-black text-sm transition-all ${
                  reached ? "bg-novatus-green text-black" : "bg-novatus-card-2 text-novatus-text-3 border border-novatus"
                } ${current ? "ring-4 ring-novatus-green/30" : ""}`}>
                  {i + 1}
                </div>
                <div className="flex-1 flex items-center justify-between">
                  <div>
                    <p className={`font-display font-bold ${reached ? "text-white" : "text-novatus-text-3"}`}>{l.name}</p>
                    <p className="text-xs text-novatus-text-3">ab {l.min} XP</p>
                  </div>
                  {current && <span className="text-xs px-3 py-1.5 rounded-full bg-novatus-green/15 text-novatus-green border border-novatus-green/40 font-semibold">Du bist hier · {xp} XP</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
        <div className="nv-card p-6 nv-card-hover animate-slide-up delay-2" data-testid="stat-total">
          <p className="label-eyebrow">Trainings gesamt</p>
          <p className="font-display font-black text-4xl text-white mt-2">{progress.total_sessions}</p>
        </div>
        <div className="nv-card p-6 nv-card-hover animate-slide-up delay-2" data-testid="stat-avg">
          <p className="label-eyebrow">Ø Score</p>
          <p className="font-display font-black text-4xl text-novatus-green mt-2">{progress.average_score}</p>
        </div>
        <div className="nv-card p-6 nv-card-hover animate-slide-up delay-3" data-testid="stat-xp">
          <p className="label-eyebrow">XP gesammelt</p>
          <p className="font-display font-black text-4xl text-white mt-2">{xp}</p>
        </div>
      </div>

      <div className="mt-10">
        <h2 className="font-display font-bold text-2xl text-white">Trainings-Historie</h2>
        <div className="mt-4 space-y-3">
          {(progress.recent_sessions || []).length === 0 ? (
            <p className="text-sm text-novatus-text-3">Noch keine Trainings absolviert.</p>
          ) : progress.recent_sessions.map((s, i) => (
            <div key={s.id || i} className="nv-card nv-card-hover p-4 flex items-center justify-between gap-4 cursor-pointer"
              onClick={() => navigate(`/training/session/${s.id}/feedback`)}
              data-testid={`history-${i}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-novatus-card-2 border border-novatus flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-novatus-text-2" />
                </div>
                <div>
                  <p className="font-semibold text-white">{s.scenario_title}</p>
                  <p className="text-xs text-novatus-text-3">{new Date(s.ended_at).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-2.5 py-1 rounded-md bg-novatus-card-2 border border-novatus text-xs font-mono text-novatus-green">+{s.xp_earned} XP</span>
                <div className="font-display font-black text-2xl text-white w-12 text-right">{s.score}</div>
                <TrendingUp className="w-4 h-4 text-novatus-text-3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Progress;
